import { useCallback, useEffect, useState } from 'react';
import { Card, DataTable, Modal, FormField, FormSelect, TextInput, Toggle, StatusBadge, EmptyState } from '@/components/ui';
import type { Tone } from '@/components/ui/StatusBadge';
import { Icon } from '@/components/ui/Icon';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { adminApi } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';

interface Did {
  id: number;
  number: string;
  type: string;
  route_type?: string | null;
  route_target?: string | null;
  recording_enabled: boolean;
  burned_at?: string | null;
  burned_reason?: string | null;
}

interface RouteOption { value: string; label: string }

const TYPE_LABEL: Record<string, string> = { landline: 'קווי', virtual: 'וירטואלי', mobile: 'נייד', tollfree: 'חינם' };
const ROUTE_LABEL: Record<string, string> = { queue: 'תור', ivr: 'IVR', ext: 'שלוחה', voicemail: 'תא קולי' };
// צבע תגית היעד בעמודת "משויך ל" — כמו בעיצוב (תפריט כחול, תור כתום, נציג אפור).
const ROUTE_TONE: Record<string, Tone> = { ivr: 'info', queue: 'warn', ext: 'neutral', voicemail: 'neutral' };

const EMPTY_CREATE = { number: '', type: 'virtual', recording_enabled: true };

export function Dids({ embedded = false }: { embedded?: boolean }) {
  const [rows, setRows] = useState<Did[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Did | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Did | null>(null);
  // הוספת מספר = תפיסה ממאגר הפלטפורמה בלבד (למספרים יש תמחור שהסופר-אדמין
  // קבע והוא מצטרף לחיוב) — אין הזנה ידנית.
  const [pool, setPool] = useState<{ id: number; number: string; type: string; monthly_price_cents?: number; purchase_price_cents?: number }[]>([]);
  const [poolLoading, setPoolLoading] = useState(false);
  const [claiming, setClaiming] = useState<number | null>(null);

  // route target options keyed by route_type
  const [queues, setQueues] = useState<RouteOption[]>([]);
  const [ivrs, setIvrs] = useState<RouteOption[]>([]);
  const [exts, setExts] = useState<RouteOption[]>([]);

  const load = useCallback(() => {
    setLoading(true);
    void adminApi
      .dids(q || undefined)
      .then((res) => setRows(res.data as Did[]))
      .catch(() => toast('שגיאה בטעינת מספרים', 'danger'))
      .finally(() => setLoading(false));
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    void adminApi.queues().then((r) => setQueues((r.data as { id: number; name: string; ext: string }[]).map((x) => ({ value: x.ext, label: `${x.name} (${x.ext})` })))).catch(() => {});
    // ניתוב IVR מצביע ישירות על תפריט (IvrMenu.id) — ה-runtime מקבל את
    // ה-id ב-Stasis args (handleIvr עושה IvrMenu::find).
    void adminApi.ivr().then((r) => setIvrs((r.data as { id: number; name: string }[]).map((x) => ({ value: String(x.id), label: x.name })))).catch(() => {});
    void adminApi.extensions('user').then((r) => setExts((r.data as { ext: string; name?: string }[]).map((x) => ({ value: x.ext, label: x.name ? `${x.name} (${x.ext})` : x.ext })))).catch(() => {});
  }, []);

  function openAdd() {
    setCreateOpen(true);
    setPoolLoading(true);
    void adminApi.availableDids()
      .then(setPool)
      .catch(() => setPool([]))
      .finally(() => setPoolLoading(false));
  }

  async function claim(id: number) {
    setClaiming(id);
    try {
      const did = await adminApi.claimDid(id);
      toast(`המספר ${did?.number ?? ''} נוסף לעסק`, 'ok');
      setPool((prev) => prev.filter((p) => p.id !== id));
      load();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בתפיסת המספר', 'danger');
    } finally {
      setClaiming(null);
    }
  }

  async function remove(d: Did) {
    try {
      await adminApi.deleteDid(d.id);
      toast('המספר שוחרר חזרה למאגר', 'ok');
      setDeleteTarget(null);
      setRows((prev) => prev.filter((r) => r.id !== d.id));
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  async function unburn(d: Did) {
    try {
      await adminApi.unburnDid(d.id);
      toast('השריפה שוחררה — המספר זמין שוב', 'ok');
      setRows((prev) => prev.map((r) => (r.id === d.id ? { ...r, burned_at: null, burned_reason: null } : r)));
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בשחרור השריפה', 'danger');
    }
  }

  // Resolve a raw route_target (ext/queue ext/ivr id) to a human-readable name.
  function resolveTarget(d: Did): string {
    if (!d.route_type || !d.route_target) return '—';
    const pool = d.route_type === 'queue' ? queues : d.route_type === 'ivr' ? ivrs : d.route_type === 'ext' ? exts : [];
    const found = pool.find((o) => o.value === d.route_target);
    return found?.label ?? d.route_target;
  }

  const cols: DataTableColumn<Did>[] = [
    { key: 'number', he: 'מספר', render: (r) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span dir="ltr" style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.number}</span>
        {r.burned_at && (
          <span title={r.burned_reason ?? 'המספר סומן כשרוף'}>
            <StatusBadge tone="danger" size="sm" dot>שרוף</StatusBadge>
          </span>
        )}
      </span>
    ) },
    { key: 'type', he: 'סוג', render: (r) => TYPE_LABEL[r.type] ?? r.type },
    {
      // ניתוב + יעד בעמודה אחת: תגית סוג היעד ולידה השם שלו.
      key: 'route_type',
      he: 'משויך ל',
      render: (r) => (r.route_type ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <StatusBadge tone={ROUTE_TONE[r.route_type] ?? 'neutral'} size="sm">{ROUTE_LABEL[r.route_type] ?? r.route_type}</StatusBadge>
          {r.route_type !== 'voicemail' && <span>{resolveTarget(r)}</span>}
        </span>
      ) : (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <StatusBadge tone="danger" size="sm">לא משויך</StatusBadge>
          <span style={{ color: 'var(--muted)' }}>—</span>
        </span>
      )),
    },
    { key: 'recording_enabled', he: 'הקלטה', render: (r) => (r.recording_enabled ? <StatusBadge tone="ok" size="sm" dot>מוקלט</StatusBadge> : <span style={{ color: 'var(--muted)' }}>—</span>) },
    {
      key: 'actions',
      he: '',
      align: 'end',
      render: (r) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          {r.burned_at && <button type="button" className="btn" onClick={() => unburn(r)}>שחרר שריפה</button>}
          <button type="button" className="btn soft" onClick={() => setEditTarget(r)}>ניתוב</button>
          <button type="button" className="btn ghost" style={{ color: 'var(--danger)' }} onClick={() => setDeleteTarget(r)}>שחרר</button>
        </div>
      ),
    },
  ];

  return (
    <div className="stack" style={embedded ? undefined : { padding: 'var(--gap)' }}>
      {!embedded && <h2 className="sr-only">מספרי DID</h2>}
      {/* סרגל כלים כמו בעיצוב: סיכום + חיפוש בצד אחד, הכפתור הראשי בצד השני. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>
          {loading ? '\u00a0' : `${rows.length} מספרים`}
        </span>
        <div style={{ position: 'relative', flex: '0 1 240px', minWidth: 160 }}>
          <span style={{ position: 'absolute', insetInlineStart: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', pointerEvents: 'none', display: 'flex' }}>
            <Icon name="search" size={15} />
          </span>
          <TextInput type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="חיפוש לפי מספר…" aria-label="חיפוש לפי מספר" style={{ height: 38, paddingInlineStart: 34 }} />
        </div>
        <button type="button" className="btn primary" onClick={openAdd} style={{ marginInlineStart: 'auto' }}>+ הוסף מספר</button>
      </div>
      <Card padded={false} style={{ ['--d' as string]: '.1s' }}><DataTable<Did> columns={cols} rows={rows} loading={loading} onRowClick={setEditTarget} emptyState={<EmptyState icon="info" title="אין עדיין מספרי DID" description="הוסף מספר ראשון כדי לקבל ולנתב שיחות נכנסות." action={<button type="button" className="btn primary" onClick={openAdd}>+ הוסף מספר</button>} />} /></Card>

      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="הוספת מספר לעסק"
        footer={<button type="button" className="btn" onClick={() => setCreateOpen(false)}>סגור</button>}>
        <div className="stack">
          <p style={{ margin: 0, color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
            בחר מספר פנוי ממאגר המערכת. העלות החודשית מצטרפת לחיוב החודשי של העסק,
            ועלות הקנייה (אם קיימת) נגבית חד-פעמית בחודש ההוספה.
          </p>
          {poolLoading ? (
            <p style={{ color: 'var(--muted)' }}>טוען מספרים זמינים…</p>
          ) : pool.length === 0 ? (
            <p style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
              אין כרגע מספרים פנויים במאגר. פנה למנהל המערכת להוספת מספרים.
            </p>
          ) : (
            <div className="stack" style={{ gap: 6, maxHeight: 340, overflow: 'auto' }}>
              {pool.map((p) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: '1px solid var(--line)', borderRadius: 'var(--r-2)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span dir="ltr" style={{ fontFamily: 'var(--font-mono)' }}>{p.number}</span>
                    <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                      {TYPE_LABEL[p.type] ?? p.type}
                      {' · '}₪{(((p.monthly_price_cents ?? 0)) / 100).toLocaleString('he-IL')} לחודש
                      {(p.purchase_price_cents ?? 0) > 0 && <> {' + '}₪{((p.purchase_price_cents ?? 0) / 100).toLocaleString('he-IL')} חד-פעמי</>}
                    </div>
                  </div>
                  <button type="button" className="btn primary" disabled={claiming === p.id} onClick={() => claim(p.id)}>
                    {claiming === p.id ? 'מוסיף…' : 'הוסף לעסק'}
                  </button>
                </div>
              ))}
            </div>
          )}
          <p style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', margin: 0 }}>ניתוב המספר (תור / שלוחה / IVR / תא קולי) נקבע לאחר ההוספה בלחיצה על המספר.</p>
        </div>
      </Modal>

      {editTarget && (
        <EditDid
          did={editTarget}
          routeOptions={{ queue: queues, ivr: ivrs, ext: exts }}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
        />
      )}

      {deleteTarget && (
        <Modal isOpen onClose={() => setDeleteTarget(null)} title="שחרור מספר"
          footer={<><button type="button" className="btn" onClick={() => setDeleteTarget(null)}>ביטול</button><button type="button" className="btn danger" onClick={() => remove(deleteTarget)}>שחרר</button></>}>
          <p>לשחרר את המספר <strong dir="ltr">{deleteTarget.number}</strong> חזרה למאגר המערכת? שיחות נכנסות אליו לא ינותבו יותר, והחיוב החודשי עליו ייפסק.</p>
        </Modal>
      )}
    </div>
  );
}

function EditDid({ did, routeOptions, onClose, onSaved }: {
  did: Did;
  routeOptions: { queue: RouteOption[]; ivr: RouteOption[]; ext: RouteOption[] };
  onClose: () => void;
  onSaved: () => void;
}) {
  const [routeType, setRouteType] = useState(did.route_type ?? '');
  const [routeTarget, setRouteTarget] = useState(did.route_target ?? '');
  const [recording, setRecording] = useState(did.recording_enabled);
  const [saving, setSaving] = useState(false);

  // when route type changes, reset target unless still valid
  function changeRouteType(rt: string) {
    setRouteType(rt);
    setRouteTarget('');
  }

  const targetOptions: RouteOption[] | null =
    routeType === 'queue' ? routeOptions.queue :
    routeType === 'ivr' ? routeOptions.ivr :
    routeType === 'ext' ? routeOptions.ext :
    null; // voicemail / none = no target picker

  async function save() {
    setSaving(true);
    try {
      await adminApi.updateDid(did.id, {
        route_type: routeType || null,
        route_target: routeType && routeType !== 'voicemail' ? (routeTarget || null) : null,
        recording_enabled: recording,
      });
      toast('עודכן', 'ok');
      onSaved();
    } catch {
      toast('שגיאה בעדכון', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={`ניתוב מספר ${did.number}`}
      footer={<><button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button><button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'שומר…' : 'שמור'}</button></>}>
      <div className="stack">
        <FormField label="סוג ניתוב">
          <FormSelect value={routeType} onChange={(e) => changeRouteType(e.target.value)}>
            <option value="">— ללא ניתוב —</option>
            {Object.entries(ROUTE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </FormSelect>
        </FormField>
        {targetOptions && (
          <FormField label="יעד">
            <FormSelect value={routeTarget} onChange={(e) => setRouteTarget(e.target.value)}>
              <option value="">— בחר יעד —</option>
              {targetOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </FormSelect>
          </FormField>
        )}
        <Toggle checked={recording} onChange={setRecording} label="הקלטת שיחות למספר זה" />
      </div>
    </Modal>
  );
}
